import {
  Module,
  VuexModule,
  Action,
  Mutation,
  getModule
} from 'vuex-module-decorators'
import Vue from 'vue'
import axios from 'axios'

import store from '@/store'
import dashboardModule from '@/store/dashboard'
import listModule, { ListType } from '@/store/list'
import whitelabelModule from '@/store/whitelabel'
import { Company } from '@/types/Company'
import { User } from '@/types/User'
import { Whitelabel } from '@/types/Whitelabel'
import { CompanyType, CompanyTypeText } from '@/utils/enums'
import { isEmpty } from 'lodash'

const name = 'company'

@Module({
  namespaced: true,
  dynamic: true,
  name,
  store
})
class CompanyModule extends VuexModule {
  private currentCompany?: Company
  private allCompanies: Partial<Company>[] = []

  private currentPartner?: User
  private allPartners: any[] = []

  private get user(): User {
    return this.context.rootState.auth.currentUser
  }

  private get whitelabel(): Whitelabel {
    return whitelabelModule.whitelabel
  }

  get company(): Company | undefined {
    return this.currentCompany
  }

  get companies(): Partial<Company>[] {
    return this.allCompanies
  }

  get partner(): User | undefined {
    return this.currentPartner
  }

  get partners(): any[] {
    return this.allPartners
  }

  get companyTypes(): string[][] {
    return Object.entries(CompanyType).map(entry => [
      CompanyTypeText[entry[0]],
      entry[1]
    ])
  }

  @Mutation
  setCurrentCompany(payload: Company) {
    Vue.set(this, 'currentCompany', payload)
  }

  @Mutation
  setAllCompanies(payload: Partial<Company>[]) {
    Vue.set(this, 'allCompanies', payload)
  }

  @Mutation
  setCurrentPartner(payload: User) {
    Vue.set(this, 'currentPartner', payload)
  }

  @Mutation
  setAllPartners(payload: User[]) {
    Vue.set(this, 'allPartners', payload)
  }

  @Action({ rawError: true })
  async fetch(id: string) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/company/${id}`
      )
      await this.context.commit('setCurrentCompany', data)
    } catch (error) {
      dashboardModule.newError('Unternehmen konnte nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchAll(params: {
    limit: number
    q?: string
    page?: number
    lean?: boolean
    sortBy?: string
    sortDesc?: boolean
  }) {
    try {
      if (!isEmpty(listModule.allFilters[ListType.Company])) {
        Object.assign(params, {
          filter: listModule.allFilters[ListType.Company]
        })
      }
      const { data } = await axios.post(
        `/admin/${this.whitelabel.slug}/company/list`,
        params
      )
      await this.context.commit('setAllCompanies', data.companies || data)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Unternehmen konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async create(payload: { whitelabel: string; company: Partial<Company> }) {
    try {
      const { data } = await axios.post(
        `/admin/${payload.whitelabel}/company`,
        payload.company
      )
      this.context.commit('setCurrentCompany', data)
      this.fetchAll({ limit: listModule.perPage })
      dashboardModule.newMessage('Unternehmen wurde erfolgreich erstellt')
    } catch (error) {
      dashboardModule.newError('Unternehmen konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  async update(payload: { whitelabel: string; company: Partial<Company> }) {
    try {
      const { data } = await axios.put(
        `/admin/${payload.whitelabel}/company/${payload.company._id}`,
        payload.company
      )
      this.context.commit('setCurrentCompany', data.company)
      dashboardModule.newMessage('Unternehmen wurde erfolgreich aktualisiert')
    } catch (error) {
      dashboardModule.newError('Unternehmen konnte nicht aktualisiert werden')
    }
  }

  @Action({ rawError: true })
  async fetchAllPartners(params: {
    limit: number
    q?: string
    page?: number
    sortBy?: string
    sortDesc?: boolean
  }) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/company/${this.company!._id}/partner`,
        {
          params
        }
      )
      this.context.commit('setAllPartners', data.partner)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Partner konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchAllPartnerCompanies() {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/partner/${this.user.id}/company`
      )
      this.context.commit('setAllCompanies', data.companies)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Unternehmen konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async linkPartner(payload: {
    whitelabel: string
    email: string
    isLight: boolean
  }) {
    try {
      await axios.post(
        `/admin/${payload.whitelabel}/company/${this.company!._id}/partner`,
        { email: payload.email, isLight: payload.isLight }
      )
      this.fetchAllPartners({ limit: listModule.perPage })
      dashboardModule.newMessage('Partner wurde erfolgreich erstellt')
    } catch (error) {
      dashboardModule.newError('Partner konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  async unlinkPartner(payload: { whitelabel: string; userId: string }) {
    try {
      await axios.put(
        `/admin/${payload.whitelabel}/company/${this.company!._id}/partner`,
        {
          userId: payload.userId
        }
      )
      this.fetchAllPartners({ limit: listModule.perPage })
      dashboardModule.newMessage('Partner wurde erfolgreich gelöscht')
    } catch (error) {
      dashboardModule.newError('Partner konnte nicht gelöscht werden')
    }
  }

  @Action({ rawError: true })
  async getCompany(id: string) {
    const { data } = await axios.get(
      `/admin/${this.whitelabel.slug}/company/${id}`
    )
    return data
  }

  @Action({ rawError: true })
  setCompany(payload: Company) {
    this.context.commit('setCurrentCompany', payload)
  }

  @Action({ rawError: true })
  setCompanies(payload: Company[]) {
    this.context.commit('setAllCompanies', payload)
  }

  @Action({ rawError: true })
  setPartner(payload: User) {
    this.context.commit('setCurrentPartner', payload)
  }

  @Action({ rawError: true })
  setPartners(payload: User[]) {
    this.context.commit('setAllPartners', payload)
  }
}

export default getModule(CompanyModule)
